import React, { useState, useRef, useEffect } from "react";
import { createPortal } from "react-dom";

interface TooltipProps {
  children: React.ReactNode;
  content: string; // prebuilt multi-line text (hoverText)
  projectId: string;
  original_file: string;
  sheet_name?: string;
  start_row?: number;
  page_number?: string;
  file_id: string;
  // optional web fields
  url?: string;
  title?: string;
  // document/tabular modals
  showDocxModal: boolean;
  setShowDocxModal: (show: boolean) => void;
  docxContent: string;
  setDocxContent: (content: string) => void;
  showExcelModal: boolean;
  setShowExcelModal: (show: boolean) => void;
  excelData: any[][];
  setExcelData: (data: any[][]) => void;
}

const clamp = (val: number, min: number, max: number) =>
  Math.max(min, Math.min(max, val));

const Tooltip: React.FC<TooltipProps> = ({
  content,
  url,
  children,
  projectId,
  original_file,
  sheet_name,
  start_row,
  page_number,
  file_id,
  showDocxModal,
  setShowDocxModal,
  docxContent,
  setDocxContent,
  showExcelModal,
  setShowExcelModal,
  excelData,
  setExcelData,
}) => {
  const [visible, setVisible] = useState(false);
  const [pos, setPos] = useState<{ top: number; left: number; ready: boolean }>(
    {
      top: -9999,
      left: -9999,
      ready: false,
    }
  );

  // refs
  const triggerRef = useRef<HTMLSpanElement | null>(null);
  const tooltipRef = useRef<HTMLDivElement | null>(null);

  // measure & position tooltip relative to trigger
  const updatePosition = () => {
    if (!triggerRef.current || !tooltipRef.current) return;

    const triggerRect = triggerRef.current.getBoundingClientRect();
    const tip = tooltipRef.current;

    // Temporarily make it measurable
    const prevVis = tip.style.visibility;
    const prevTop = tip.style.top;
    const prevLeft = tip.style.left;
    const prevPos = tip.style.position;

    tip.style.visibility = "hidden";
    tip.style.position = "fixed";
    tip.style.top = "0px";
    tip.style.left = "0px";

    const tipWidth = tip.offsetWidth;
    const tipHeight = tip.offsetHeight;

    // Preferred above the trigger, horizontally centered
    const offset = 10;
    let top = triggerRect.top - tipHeight - offset;
    let left = triggerRect.left + triggerRect.width / 2 - tipWidth / 2;

    // If above overflows, place below
    if (top < 8) {
      top = triggerRect.bottom + offset;
    }

    // Clamp horizontally to viewport
    const vw = window.innerWidth;
    const vh = window.innerHeight;

    left = clamp(left, 8, vw - tipWidth - 8);
    top = clamp(top, 8, vh - tipHeight - 8);

    // Restore temporary styles
    tip.style.visibility = prevVis;
    tip.style.position = prevPos;
    tip.style.top = prevTop;
    tip.style.left = prevLeft;

    setPos({ top, left, ready: true });
  };

  // Reposition when opening and on env changes
  useEffect(() => {
    if (!visible) return;

    updatePosition();

    const handler = () => updatePosition();
    window.addEventListener("scroll", handler, { passive: true });
    window.addEventListener("resize", handler, { passive: true });

    const ro = new ResizeObserver(handler);
    if (triggerRef.current) ro.observe(triggerRef.current);

    return () => {
      window.removeEventListener("scroll", handler);
      window.removeEventListener("resize", handler);
      ro.disconnect();
    };
  }, [visible, content]);

  return (
    <span
      ref={triggerRef}
      className="relative mt-[-0.2rem!important] mx-[5px] mb-[-2px!important] cursor-pointer"
      onMouseEnter={() => setVisible(true)}
      onMouseLeave={() => setVisible(false)}
      onClick={(e) => {
        if (url) {
          e.stopPropagation();
          try {
            window.open(url, "_blank", "noopener,noreferrer");
          } catch {
            // ignore
          }
        }
      }}
      title={url ? "Open source in new tab" : undefined}
    >
      {children}

      {visible &&
        createPortal(
          <div
            ref={tooltipRef}
            className="p-2 bg-black text-white text-xs rounded shadow-lg z-[9999] whitespace-pre-line"
            style={{
              position: "fixed",
              top: pos.ready ? pos.top : -9999,
              left: pos.ready ? pos.left : -9999,
              maxWidth: 320,
              lineHeight: 1.2,
              pointerEvents: "none",
            }}
          >
            {content}
          </div>,
          document.body
        )}
    </span>
  );
};

export default Tooltip;
